import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Save,
  Trash2,
  ArrowLeft,
  Copy,
  Layers,
  Wrench,
  Package,
  Ruler,
  UserCircle,
  Download,
  Image as ImageIcon,
  Upload,
  X,
  PenLine,
  GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import {
  listProfilePresets,
  createProfilePreset,
  updateProfilePreset,
  deleteProfilePreset,
  reorderProfilePresets,
  getSettings,
  updateSettings,
  listPdfExports,
} from "@/lib/api";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function emptyPreset(name = "Novo perfil") {
  return {
    name,
    profile_thickness_mm: 45,
    notes: "",
    height_mm: 2600,
    width_mm: 1200,
    customer: {
      doorsealing_price_per_m: 0,
      centerprofile_price_per_m: 0,
      right_doorsealing_price_per_m: 0,
      right_centerprofile_price_per_m: 0,
      cutting_time_min: 0,
      corner_gluing_time_min: 0,
      mounting_time_min: 0,
      steelband_price: 0,
      drilling_time_min: 0,
      popniets_cost: 0,
    },
    primo: {
      doorsealing_price_per_m: 0,
      centerprofile_price_per_m: 0,
      right_doorsealing_price_per_m: 0,
      right_centerprofile_price_per_m: 0,
      welding_cutting_time_min: 0,
      gluing_time_min: 0,
      mounting_time_min: 0,
      glue_cost: 0,
    },
  };
}

function NumberField({ label, value, onChange, unit = "", testId }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-[11px] font-semibold text-[color:var(--text)] font-body">{label}</Label>
      <div className="relative">
        <Input
          type="number"
          step="0.01"
          value={value ?? ""}
          onChange={(e) => onChange(Number(e.target.value))}
          data-testid={testId}
          className="bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text)] font-display font-semibold text-sm h-9 px-2.5 rounded pr-9"
        />
        {unit && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-mono text-[color:var(--text-mute)] pointer-events-none">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// SortablePresetItem — one row in the drag-and-drop list.
// Uses @dnd-kit/sortable. Keeps its own click-to-select + duplicate
// buttons; the drag handle is a dedicated `GripVertical` icon so
// clicking anywhere else on the row still selects the preset for
// editing on the right panel.
// ---------------------------------------------------------------
function SortablePresetItem({ preset, active, onSelect, onDuplicate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: preset.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`preset-row-${preset.id}`}
      className={[
        "w-full rounded border transition-colors flex items-stretch",
        active
          ? "bg-[color:var(--brand-soft)] border-[color:var(--brand)]"
          : "bg-transparent border-[color:var(--border)] hover:border-[color:var(--brand)]",
      ].join(" ")}
    >
      {/* Drag handle */}
      <button
        type="button"
        aria-label="Arrastar para reordenar"
        data-testid={`drag-handle-${preset.id}`}
        {...attributes}
        {...listeners}
        className="flex items-center justify-center px-2 cursor-grab active:cursor-grabbing text-[color:var(--text-mute)] hover:text-[color:var(--brand)] touch-none select-none"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Body — clickable to select */}
      <button
        type="button"
        onClick={() => onSelect(preset)}
        data-testid={`preset-item-${preset.id}`}
        className="flex-1 min-w-0 text-left px-1 py-2"
      >
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-sm text-[color:var(--text)] truncate">
            {preset.name}
          </div>
          <div className="text-[10px] font-mono text-[color:var(--text-mute)]">
            {Number(preset.profile_thickness_mm).toFixed(0)} mm · {Number(preset.height_mm ?? 2600).toFixed(0)}×{Number(preset.width_mm ?? 1200).toFixed(0)}
            {preset.is_system ? " · sistema" : " · custom"}
          </div>
        </div>
      </button>

      {/* Duplicate icon */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDuplicate(preset); }}
        className="flex items-center justify-center px-2 text-[color:var(--text-mute)] hover:text-[color:var(--brand)]"
        title="Duplicar"
        data-testid={`duplicate-${preset.id}`}
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function AdminPresetsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // Technician settings
  const [techSettings, setTechSettings] = useState({ tech_name: "", tech_email: "", tech_phone: "", tech_greeting: "", tech_signature_image: "" });
  const [techSaving, setTechSaving] = useState(false);
  const sigFileInputRef = useRef(null);
  const [sigUploadError, setSigUploadError] = useState("");

  // Active tab
  const [activeTab, setActiveTab] = useState("profiles");

  // PDF export history
  const [exportHistory, setExportHistory] = useState([]);
  const [exportSearch, setExportSearch] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await listProfilePresets();
      setRows(list);
    } catch {
      toast.error("Falha ao carregar perfis");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Drag-and-drop sensors + handler ----------
  const sensors = useSensors(
    // small activation distance so a plain click on the row still counts
    // as a click (opens editor), only pointer-drag longer than 6px starts DnD.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(rows, oldIndex, newIndex);
    // Assign fresh, evenly-spaced order values so future inserts have room.
    const withOrder = reordered.map((r, i) => ({ ...r, order: (i + 1) * 10 }));
    // Optimistic UI update
    setRows(withOrder);
    try {
      await reorderProfilePresets(withOrder.map((r) => ({ id: r.id, order: r.order })));
    } catch (err) {
      console.error("reorder failed", err);
      toast.error("Erro ao guardar nova ordem");
      // Revert on failure
      refresh();
    }
  };

  useEffect(() => {
    refresh();
    getSettings().then(setTechSettings).catch(() => {});
    listPdfExports().then(setExportHistory).catch(() => {});
  }, []);

  const selectPreset = (row) => {
    setSelected(row.id);
    setDraft(JSON.parse(JSON.stringify(row)));
  };

  const startNew = () => {
    setSelected("__new__");
    setDraft(emptyPreset(`Perfil personalizado ${rows.length + 1}`));
  };

  const duplicate = (row) => {
    const copy = JSON.parse(JSON.stringify(row));
    delete copy.id;
    delete copy.created_at;
    delete copy.updated_at;
    copy.is_system = false;
    copy.name = `${row.name} (cópia)`;
    setSelected("__new__");
    setDraft(copy);
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!draft.name?.trim()) {
      toast.error("O nome é obrigatório");
      return;
    }
    setSaving(true);
    try {
      if (selected === "__new__") {
        const created = await createProfilePreset({
          name: draft.name,
          profile_thickness_mm: Number(draft.profile_thickness_mm) || 0,
          notes: draft.notes || "",
          height_mm: Number(draft.height_mm) || 2600,
          width_mm: Number(draft.width_mm) || 1200,
          customer: draft.customer,
          primo: draft.primo,
        });
        toast.success("Perfil criado");
        await refresh();
        setSelected(created.id);
        setDraft(created);
      } else {
        const updated = await updateProfilePreset(selected, {
          name: draft.name,
          profile_thickness_mm: Number(draft.profile_thickness_mm) || 0,
          notes: draft.notes || "",
          height_mm: Number(draft.height_mm) || 2600,
          width_mm: Number(draft.width_mm) || 1200,
          customer: draft.customer,
          primo: draft.primo,
        });
        toast.success("Perfil atualizado");
        await refresh();
        setDraft(updated);
      }
    } catch (e) {
      console.error(e);
      toast.error("Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft?.id) return;
    try {
      await deleteProfilePreset(draft.id);
      toast.success("Perfil eliminado");
      setSelected(null);
      setDraft(null);
      await refresh();
    } catch {
      toast.error("Erro ao eliminar");
    }
  };

  const patchCustomer = (patch) => setDraft((d) => ({ ...d, customer: { ...d.customer, ...patch } }));
  const patchPrimo = (patch) => setDraft((d) => ({ ...d, primo: { ...d.primo, ...patch } }));

  // -----------------------------------------------------------------
  // Signature image upload — reads a file → base64 data URL → state
  // -----------------------------------------------------------------
  const MAX_SIG_SIZE_KB = 500; // hard limit (base64 payloads stay small in Mongo)

  const handleSignatureFile = (file) => {
    setSigUploadError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setSigUploadError("Ficheiro tem de ser uma imagem (PNG, JPG, SVG, WEBP).");
      return;
    }
    if (file.size > MAX_SIG_SIZE_KB * 1024) {
      setSigUploadError(`Imagem demasiado grande (max ${MAX_SIG_SIZE_KB} KB).`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result || "";
      setTechSettings((s) => ({ ...s, tech_signature_image: String(dataUrl) }));
    };
    reader.onerror = () => setSigUploadError("Erro ao ler o ficheiro.");
    reader.readAsDataURL(file);
  };

  const onSigFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    handleSignatureFile(f);
    // reset the input so re-selecting the same file still triggers change
    if (e.target) e.target.value = "";
  };

  const onSigDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files && e.dataTransfer.files[0];
    if (f) handleSignatureFile(f);
  };

  const removeSignatureImage = () => {
    setTechSettings((s) => ({ ...s, tech_signature_image: "" }));
    setSigUploadError("");
  };

  const saveTechSettings = async () => {
    setTechSaving(true);
    try {
      const updated = await updateSettings({
        tech_name: techSettings.tech_name,
        tech_email: techSettings.tech_email,
        tech_phone: techSettings.tech_phone,
        tech_greeting: techSettings.tech_greeting,
        tech_signature_image: techSettings.tech_signature_image || "",
      });
      setTechSettings(updated);
      toast.success("Assinatura guardada");
    } catch {
      toast.error("Erro ao guardar assinatura");
    } finally {
      setTechSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg)]">
      <TopBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-[color:var(--text-mute)] hover:text-[color:var(--brand)] flex items-center gap-1 text-xs font-mono uppercase tracking-widest"
              data-testid="back-home-link"
            >
              <ArrowLeft className="w-3 h-3" />
              home
            </Link>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[color:var(--brand)]">Admin</div>
              <h1 className="font-display text-2xl font-bold text-[color:var(--text)] leading-none">
                Definições
              </h1>
            </div>
          </div>
          {activeTab === "profiles" && (
            <Button
              onClick={startNew}
              data-testid="new-preset-btn"
              className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dim)] text-black font-semibold text-xs h-9"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Novo perfil
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList
            data-testid="admin-tabs"
            className="grid w-full grid-cols-3 max-w-2xl bg-[color:var(--surface-2)] border border-[color:var(--border)] p-1 rounded"
          >
            <TabsTrigger
              value="profiles"
              data-testid="tab-profiles"
              className="text-xs font-mono uppercase tracking-widest data-[state=active]:bg-[color:var(--brand)] data-[state=active]:text-black data-[state=active]:shadow-none"
            >
              <Layers className="w-3.5 h-3.5 mr-1.5" />
              Perfis
            </TabsTrigger>
            <TabsTrigger
              value="signature"
              data-testid="tab-signature"
              className="text-xs font-mono uppercase tracking-widest data-[state=active]:bg-[color:var(--brand)] data-[state=active]:text-black data-[state=active]:shadow-none"
            >
              <UserCircle className="w-3.5 h-3.5 mr-1.5" />
              Assinatura
            </TabsTrigger>
            <TabsTrigger
              value="history"
              data-testid="tab-history"
              className="text-xs font-mono uppercase tracking-widest data-[state=active]:bg-[color:var(--brand)] data-[state=active]:text-black data-[state=active]:shadow-none"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Histórico
            </TabsTrigger>
          </TabsList>

          {/* ─────────────────  PROFILES TAB  ───────────────── */}
          <TabsContent value="profiles" className="mt-4" data-testid="tab-content-profiles">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* LEFT — list */}
          <aside className="card-tech p-2 lg:col-span-1 min-w-0">
            <div className="flex items-center gap-2 px-2 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)]">
              <Layers className="w-3 h-3" />
              {rows.length} perfis
            </div>
            <div className="mt-1 max-h-[70vh] overflow-y-auto pr-0.5">
              {loading && (
                <div className="p-3 text-xs text-[color:var(--text-mute)] italic">A carregar…</div>
              )}
              {!loading && rows.length === 0 && (
                <div className="p-3 text-xs text-[color:var(--text-mute)] italic">Sem perfis. Crie um novo.</div>
              )}
              {!loading && rows.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onDragEnd}
                >
                  <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1" data-testid="preset-sortable-list">
                      {rows.map((r) => (
                        <SortablePresetItem
                          key={r.id}
                          preset={r}
                          active={selected === r.id}
                          onSelect={selectPreset}
                          onDuplicate={duplicate}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </aside>

          {/* RIGHT — editor */}
          <section className="card-tech p-3 lg:p-4 lg:col-span-2 min-w-0">
            {!draft ? (
              <div className="flex items-center justify-center h-64 text-[color:var(--text-mute)] text-sm">
                Selecione um perfil à esquerda ou crie um novo.
              </div>
            ) : (
              <div className="space-y-3">
                {/* Header */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="md:col-span-2">
                    <Label className="text-[11px] font-semibold text-[color:var(--text)] font-body">Nome do perfil</Label>
                    <Input
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      data-testid="preset-name-input"
                      className="bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text)] font-display font-semibold text-sm h-9 px-2.5 rounded"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <NumberField
                    label="Espessura (mm)"
                    value={draft.profile_thickness_mm}
                    onChange={(v) => setDraft({ ...draft, profile_thickness_mm: v })}
                    unit="mm"
                    testId="preset-thickness-input"
                  />
                  <NumberField
                    label="Altura porta (mm)"
                    value={draft.height_mm ?? 2600}
                    onChange={(v) => setDraft({ ...draft, height_mm: v })}
                    unit="mm"
                    testId="preset-height-input"
                  />
                  <NumberField
                    label="Largura porta (mm)"
                    value={draft.width_mm ?? 1200}
                    onChange={(v) => setDraft({ ...draft, width_mm: v })}
                    unit="mm"
                    testId="preset-width-input"
                  />
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <div>
                    <Label className="text-[11px] font-semibold text-[color:var(--text)] font-body">Notas</Label>
                    <Textarea
                      value={draft.notes || ""}
                      onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                      className="bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text)] font-body h-9 min-h-0 rounded text-sm px-2.5 py-1"
                      data-testid="preset-notes-input"
                    />
                  </div>
                </div>

                {draft.is_system && (
                  <div className="rounded border border-[color:var(--gold)] bg-[color:var(--gold)]/10 text-[color:var(--text)] text-[11px] px-3 py-2">
                    Este é um perfil de sistema — as edições ficam guardadas. Se precisar de repor os valores originais, use o botão &quot;Reseed&quot; ou duplique este perfil.
                  </div>
                )}

                {/* Custos "Atuais" — o custo actual da solução do cliente (antes de adoptar a Primo) */}
                <div className="border border-[color:var(--border)] rounded p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-3.5 h-3.5 text-[color:var(--danger)]" />
                    <h3 className="font-display font-semibold text-sm">Custos atuais</h3>
                    <span className="text-[10px] font-mono text-[color:var(--text-mute)] uppercase tracking-widest">
                      solução em uso hoje
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <NumberField label="DS Frame A (€/m)" value={draft.customer.doorsealing_price_per_m} onChange={(v) => patchCustomer({ doorsealing_price_per_m: v })} unit="€" testId="ct-ds-a" />
                    <NumberField label="CP Frame A (€/m)" value={draft.customer.centerprofile_price_per_m} onChange={(v) => patchCustomer({ centerprofile_price_per_m: v })} unit="€" testId="ct-cp-a" />
                    <NumberField label="DS Frame B (€/m)" value={draft.customer.right_doorsealing_price_per_m} onChange={(v) => patchCustomer({ right_doorsealing_price_per_m: v })} unit="€" testId="ct-ds-b" />
                    <NumberField label="CP Frame B (€/m)" value={draft.customer.right_centerprofile_price_per_m} onChange={(v) => patchCustomer({ right_centerprofile_price_per_m: v })} unit="€" testId="ct-cp-b" />
                    <NumberField label="Corte (min/frame)" value={draft.customer.cutting_time_min} onChange={(v) => patchCustomer({ cutting_time_min: v })} unit="min" testId="ct-cut" />
                    <NumberField label="Colagem (min/frame)" value={draft.customer.corner_gluing_time_min} onChange={(v) => patchCustomer({ corner_gluing_time_min: v })} unit="min" testId="ct-gluing" />
                    <NumberField label="Montagem (min/frame)" value={draft.customer.mounting_time_min} onChange={(v) => patchCustomer({ mounting_time_min: v })} unit="min" testId="ct-mount" />
                    <div />
                    <NumberField label="Steel band (€/frame)" value={draft.customer.steelband_price} onChange={(v) => patchCustomer({ steelband_price: v })} unit="€" testId="ct-steel" />
                    <NumberField label="Furação (min/frame)" value={draft.customer.drilling_time_min} onChange={(v) => patchCustomer({ drilling_time_min: v })} unit="min" testId="ct-drill" />
                    <NumberField label="Rebites (€/frame)" value={draft.customer.popniets_cost} onChange={(v) => patchCustomer({ popniets_cost: v })} unit="€" testId="ct-riv" />
                  </div>
                </div>

                {/* Primo section */}
                <div className="border border-[color:var(--brand)]/50 rounded p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench className="w-3.5 h-3.5 text-[color:var(--brand)]" />
                    <h3 className="font-display font-semibold text-sm">Solução Primo</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <NumberField label="DS Frame A (€/m)" value={draft.primo.doorsealing_price_per_m} onChange={(v) => patchPrimo({ doorsealing_price_per_m: v })} unit="€" testId="pr-ds-a" />
                    <NumberField label="CP Frame A (€/m)" value={draft.primo.centerprofile_price_per_m} onChange={(v) => patchPrimo({ centerprofile_price_per_m: v })} unit="€" testId="pr-cp-a" />
                    <NumberField label="DS Frame B (€/m)" value={draft.primo.right_doorsealing_price_per_m} onChange={(v) => patchPrimo({ right_doorsealing_price_per_m: v })} unit="€" testId="pr-ds-b" />
                    <NumberField label="CP Frame B (€/m)" value={draft.primo.right_centerprofile_price_per_m} onChange={(v) => patchPrimo({ right_centerprofile_price_per_m: v })} unit="€" testId="pr-cp-b" />
                    <NumberField label="Soldadura & corte (min)" value={draft.primo.welding_cutting_time_min} onChange={(v) => patchPrimo({ welding_cutting_time_min: v })} unit="min" testId="pr-weld" />
                    <NumberField label="Colagem (min)" value={draft.primo.gluing_time_min} onChange={(v) => patchPrimo({ gluing_time_min: v })} unit="min" testId="pr-gluing" />
                    <NumberField label="Montagem (min)" value={draft.primo.mounting_time_min} onChange={(v) => patchPrimo({ mounting_time_min: v })} unit="min" testId="pr-mount" />
                    <NumberField label="Cola/material (€/frame)" value={draft.primo.glue_cost} onChange={(v) => patchPrimo({ glue_cost: v })} unit="€" testId="pr-glue" />
                  </div>
                </div>

                {/* Footer actions */}
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-[color:var(--border)]">
                  {draft.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-9 border-[color:var(--border-strong)] hover:border-[color:var(--danger)] hover:text-[color:var(--danger)] bg-transparent text-[color:var(--text)] text-xs"
                          data-testid="delete-preset-btn"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          Eliminar
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminar perfil?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acção é irreversível. O perfil &laquo;{draft.name}&raquo; será removido definitivamente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-preset">Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-preset-btn"
                    className="h-9 bg-[color:var(--brand)] hover:bg-[color:var(--brand-dim)] text-black font-semibold text-xs"
                  >
                    <Save className="w-3.5 h-3.5 mr-1.5" />
                    {saving ? "A guardar…" : "Guardar"}
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
          </TabsContent>

          {/* ─────────────────  SIGNATURE TAB  ───────────────── */}
          <TabsContent value="signature" className="mt-4" data-testid="tab-content-signature">
        <div className="card-tech p-4" data-testid="tech-settings-section">
          <div className="flex items-center gap-2 mb-3">
            <UserCircle className="w-4 h-4 text-[color:var(--brand)]" />
            <h2 className="font-display text-lg font-bold text-[color:var(--text)]">
              Assinatura do Técnico
            </h2>
            <span className="text-[10px] font-mono text-[color:var(--text-mute)] uppercase tracking-widest ml-auto">
              Aparece no rodapé do PDF
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-[11px] font-semibold text-[color:var(--text)] font-body">Nome</Label>
              <Input
                value={techSettings.tech_name || ""}
                onChange={(e) => setTechSettings({ ...techSettings, tech_name: e.target.value })}
                placeholder="Ex: João Silva"
                data-testid="tech-name-input"
                className="bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text)] font-display text-sm h-9 px-2.5 rounded"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[11px] font-semibold text-[color:var(--text)] font-body">Email</Label>
              <Input
                type="email"
                value={techSettings.tech_email || ""}
                onChange={(e) => setTechSettings({ ...techSettings, tech_email: e.target.value })}
                placeholder="joao@primo.pt"
                data-testid="tech-email-input"
                className="bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text)] font-display text-sm h-9 px-2.5 rounded"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[11px] font-semibold text-[color:var(--text)] font-body">Telefone</Label>
              <Input
                value={techSettings.tech_phone || ""}
                onChange={(e) => setTechSettings({ ...techSettings, tech_phone: e.target.value })}
                placeholder="+351 912 345 678"
                data-testid="tech-phone-input"
                className="bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text)] font-display text-sm h-9 px-2.5 rounded"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[11px] font-semibold text-[color:var(--text)] font-body">Saudação / fecho</Label>
              <Input
                value={techSettings.tech_greeting || ""}
                onChange={(e) => setTechSettings({ ...techSettings, tech_greeting: e.target.value })}
                placeholder="Com os melhores cumprimentos"
                data-testid="tech-greeting-input"
                className="bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text)] font-display text-sm h-9 px-2.5 rounded"
              />
            </div>
          </div>

          {/* Signature image uploader */}
          <div className="mt-5" data-testid="signature-image-block">
            <div className="flex items-center gap-2 mb-2">
              <PenLine className="w-3.5 h-3.5 text-[color:var(--brand)]" />
              <Label className="text-[11px] font-semibold text-[color:var(--text)] font-body">
                Imagem da assinatura
              </Label>
              <span className="text-[10px] font-mono text-[color:var(--text-mute)] uppercase tracking-widest">
                PNG, JPG, SVG · máx {MAX_SIG_SIZE_KB} KB
              </span>
            </div>
            <input
              ref={sigFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={onSigFileChange}
              className="hidden"
              data-testid="signature-file-input"
            />
            {techSettings.tech_signature_image ? (
              <div
                className="flex flex-col sm:flex-row items-start sm:items-center gap-3 border border-dashed border-[color:var(--border)] rounded p-3 bg-[color:var(--surface)]"
                data-testid="signature-preview-wrap"
              >
                <div className="bg-white rounded border border-[color:var(--border)] p-2 shadow-sm">
                  <img
                    src={techSettings.tech_signature_image}
                    alt="Assinatura do técnico"
                    data-testid="signature-preview-img"
                    className="max-h-24 max-w-[240px] object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[color:var(--text)] font-display">
                    Assinatura carregada.
                  </div>
                  <div className="text-[10px] font-mono text-[color:var(--text-mute)] mt-0.5">
                    Ficará no rodapé do PDF junto ao nome, email e telefone.
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => sigFileInputRef.current?.click()}
                    variant="outline"
                    data-testid="signature-replace-btn"
                    className="h-8 border-[color:var(--border-strong)] hover:border-[color:var(--brand)] bg-transparent text-[color:var(--text)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--text)] text-xs"
                  >
                    <Upload className="w-3 h-3 mr-1.5" />
                    Substituir
                  </Button>
                  <Button
                    onClick={removeSignatureImage}
                    variant="outline"
                    data-testid="signature-remove-btn"
                    className="h-8 border-[color:var(--border-strong)] hover:border-[color:var(--danger)] hover:text-[color:var(--danger)] bg-transparent text-[color:var(--text)] text-xs"
                  >
                    <X className="w-3 h-3 mr-1.5" />
                    Remover
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => sigFileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={onSigDrop}
                data-testid="signature-drop-area"
                className="w-full flex flex-col items-center justify-center gap-2 border border-dashed border-[color:var(--border)] hover:border-[color:var(--brand)] hover:bg-[color:var(--brand-soft)] rounded p-5 text-[color:var(--text-mute)] hover:text-[color:var(--brand)] transition-colors cursor-pointer"
              >
                <ImageIcon className="w-6 h-6" />
                <div className="text-xs font-display font-semibold">
                  Clique ou arraste uma imagem da assinatura
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest">
                  PNG · JPG · SVG · WEBP · máx {MAX_SIG_SIZE_KB} KB
                </div>
              </button>
            )}
            {sigUploadError && (
              <div className="mt-2 text-[11px] text-[color:var(--danger)] font-mono" data-testid="signature-upload-error">
                {sigUploadError}
              </div>
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              onClick={saveTechSettings}
              disabled={techSaving}
              data-testid="save-tech-btn"
              className="h-9 bg-[color:var(--brand)] hover:bg-[color:var(--brand-dim)] text-black font-semibold text-xs"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {techSaving ? "A guardar…" : "Guardar assinatura"}
            </Button>
          </div>
        </div>
          </TabsContent>

          {/* ─────────────────  HISTORY TAB  ───────────────── */}
          <TabsContent value="history" className="mt-4" data-testid="tab-content-history">
        {exportHistory.length === 0 ? (
          <div className="card-tech p-6 text-center text-[color:var(--text-mute)] text-sm font-display" data-testid="history-empty">
            Ainda não existem exportações registadas.
          </div>
        ) : (
          <div className="card-tech p-4" data-testid="export-history-section">
            <div className="flex items-center gap-2 mb-3">
              <Download className="w-4 h-4 text-[color:var(--brand)]" />
              <h2 className="font-display text-lg font-bold text-[color:var(--text)]">
                Histórico de Exportações
              </h2>
              <span className="text-[10px] font-mono text-[color:var(--text-mute)] uppercase tracking-widest ml-auto">
                {exportHistory.length} exportações
              </span>
            </div>

            {/* Search filter */}
            <div className="mb-3">
              <Input
                value={exportSearch}
                onChange={(e) => setExportSearch(e.target.value)}
                placeholder="Pesquisar por cliente, idioma ou técnico…"
                data-testid="export-search-input"
                className="bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text)] font-display text-sm h-9 px-3 rounded max-w-sm"
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[color:var(--border)]">
                    <th className="text-left text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] py-2 px-2">Data</th>
                    <th className="text-left text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] py-2 px-2">Cliente</th>
                    <th className="text-left text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] py-2 px-2">Idioma</th>
                    <th className="text-left text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] py-2 px-2">Técnico</th>
                  </tr>
                </thead>
                <tbody>
                  {exportHistory
                    .filter((ex) => {
                      if (!exportSearch.trim()) return true;
                      const q = exportSearch.toLowerCase();
                      return (
                        (ex.client_name || "").toLowerCase().includes(q) ||
                        (ex.language || "").toLowerCase().includes(q) ||
                        (ex.exported_by || "").toLowerCase().includes(q)
                      );
                    })
                    .map((ex) => (
                    <tr key={ex.id} className="border-b border-[color:var(--border)] last:border-0 hover:bg-[color:var(--surface-2)] transition-colors">
                      <td className="py-2 px-2 text-xs font-mono text-[color:var(--text-mute)]">
                        {new Date(ex.exported_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="py-2 px-2 font-display font-semibold text-[color:var(--text)]">
                        {ex.client_name || "—"}
                      </td>
                      <td className="py-2 px-2">
                        <span className="inline-flex items-center gap-1 bg-[color:var(--brand-soft)] text-[color:var(--brand)] text-[10px] font-mono font-bold px-2 py-0.5 rounded">
                          {ex.language?.toUpperCase() || "—"}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-xs text-[color:var(--text-mute)]">
                        {ex.exported_by || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
